<template>
  <section class="registration-sheet">
    <race-filters v-if="raceEvent" class="sheet-toolbar" :race-event="raceEvent">
      <button type="button" @click="newRaceDayParticipant">New Participant</button>
    </race-filters>
    
    <header class="sub toolbar-controls">
      <popover-inline :title="sortTitle" button-class="select" prefer="dn">
        <menu-contents v-model="sortMode" :menus="sortMenus" />
      </popover-inline>
      <toggle-switch v-model="showOnlyUnchecked" title="Only Unchecked">Only Unchecked</toggle-switch>
    </header>
    

    <div class="sheet-body" :class="{ editing: editingParticipant }">
      <section class="roster">
        <div class="summary-strip">
          <span>{{ filteredParticipants.length }} shown</span>
          <span>{{ checkedInCount }} checked in</span>
          <span>{{ participants.length - checkedInCount }} remaining</span>
        </div>

        <div class="participant-list">
          <div
            v-for="participant in filteredParticipants"
            :key="participant.id"
            class="participant-row"
            :class="{ selected: editingParticipant?.id === participant.id, checked: participant.pickedUpAt }"
          >
            <button
              type="button"
              class="check-button"
              :class="{ checked: participant.pickedUpAt }"
              :aria-pressed="!!participant.pickedUpAt"
              :title="participant.pickedUpAt ? 'Undo check in' : 'Check in'"
              @click="toggleParticipantCheckIn(participant)"
            >
              <span>{{ participant.pickedUpAt ? 'OK' : '' }}</span>
            </button>
            <bib-display :bib-number="participant.bib" :color="participant.race?.color || '#777'" />
            <span class="name">
              <strong>{{ participant.lastName }}</strong>, {{ participant.firstName }}
            </span>
            <span class="wave">{{ participant.waveId || '' }}</span>
            <span class="shirt">{{ participant.shirtSize || '' }}</span>
            <span class="notes">{{ participant.notes || '' }}</span>
            <button type="button" class="edit-button bar" @click="editParticipant(participant)">
              Edit
            </button>
          </div>
        </div>
      </section>

      <participant-editor
        v-if="editingParticipant"
        :participant="editingParticipant"
        :race-event="raceEvent"
        @close="editingParticipantId = null"
        @saved="participantSaved"
      />
    </div>
  </section>
</template>

<style lang="scss">
.registration-sheet {
  height: calc(100vh - var(--header-size) - 1rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .race-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;

    i {
      flex: 0 0 auto;
      width: 18px;
      height: 18px;
      border-radius: 50%;
    }

    h1,
    p {
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    h1 {
      font-size: 1.25rem;
      font-weight: 500;
    }

    p {
      color: var(--text-dim1-color);
    }
  }

  .toolbar-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .sheet-body {
    min-height: 0;
    flex: 1;
    display: grid;
    grid-template-columns: minmax(0, 1fr);

    &.editing {
      grid-template-columns: minmax(0, 1fr) 390px;
    }
  }

  .roster {
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .summary-strip {
    display: flex;
    gap: 1rem;
    padding: 0.65rem 1rem;
    color: var(--text-dim1-color);
    border-bottom: 1px solid var(--text-dim3-color);
  }

  .participant-list {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  .participant-row {
    width: 100%;
    min-height: 64px;
    display: grid;
    grid-template-columns: 56px 92px minmax(180px, 1fr) 82px 72px 82px minmax(120px, 0.9fr);
    align-items: center;
    gap: 0.65rem;
    padding: 0 1rem;
    margin: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    color: var(--text-color);
    text-align: left;
    border-bottom: 1px solid var(--text-dim3-color);
    font-size: 1.25rem;

    &:nth-child(2n) {
      background: var(--inset-bgcolor);
    }

    &:hover,
    &.selected {
      background: var(--row-hover-bgcolor);
    }

    &.checked  {
      .bib-display { opacity: 0.6; }
      .name {
        // text-decoration: line-through;
        color: var(--text-dim2-color);
      }
    }
    
    .check-button {
      width: 48px;
      height: 48px;
      min-width: 48px;
      margin: 0;
      padding: 0;
      display: grid;
      place-items: center;
      border-radius: var(--br);
      border: 2px solid var(--text-dim2-color);
      background: var(--input-bgcolor);
      color: var(--success-color);
      box-shadow: none;
      font-size: 0.9rem;
      font-weight: 700;

      &.checked {
//        border-color: var(--success-color);
//        background: color-mix(in srgb, var(--success-color) 20%, var(--input-bgcolor));
        border-color: var(--dark-color);
        background: var(--app-bgcolor);
        color: var(--text-dim3-color);
      }
    }

    .bib-display {
      margin: 0;
    }

    .name,
    .notes {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .wave,
    .shirt {
      color: var(--text-dim1-color);
      white-space: nowrap;
    }

    .edit-button {
      margin: 0;
      min-width: 64px;
      height: 40px;
      justify-content: center;
    }
  }
}

@media screen and (max-width: 900px) {
  .registration-sheet {
    .sheet-toolbar {
    }

    .sheet-body,
    .sheet-body.editing {
      grid-template-columns: 1fr;
    }

    .participant-editor {
      position: fixed;
      top: var(--header-height);
      right: 0;
      bottom: 0;
      width: min(420px, 100vw);
      box-shadow: -8px 0 22px rgb(0 0 0 / 35%);
    }
  }
}
</style>

<script>
import ParticipantEditor from '@/components/participant-editor.vue'
import RaceFilters from '@/components/race-filters.vue'
import eventbus from '@/eventbus.js'

export default {
  components: {
    ParticipantEditor,
    RaceFilters,
  },

  props: {
    raceEventId: {
      type: [String, Number],
      required: true,
    },
    raceId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      query: '',
      sortMode: 'name',
      showOnlyUnchecked: false,
      editingParticipantId: null,
      sortMenus: [
        { value: 'name', title: 'Last/First' },
        { value: 'bib', title: 'Bib' },
        { value: 'shirt', title: 'Shirt' },
        { value: 'wave', title: 'Wave' },
      ],
    }
  },

  computed: {
    raceEvent() {
      return this.sharedStore.state.raceEvent[Number(this.raceEventId)]
    },

    races() {
      return (this.raceEvent?.races || []).filter(Boolean)
    },

    selectedRaces() {
      const filteredRaceIds = this.sharedStore.state.filteredRaceIds || {}
      return this.races.filter(race => filteredRaceIds[race.id])
    },

    filterDisplayableRaces() {
      return this.races.filter(race => race.name !== 'Independent Add-on Order')
    },

    displayedRaces() {
      const displayableRaceIds = new Set(this.filterDisplayableRaces.map(race => race.id))
      const selectedDisplayableRaces = this.selectedRaces
        .filter(race => displayableRaceIds.has(race.id))

      return selectedDisplayableRaces.length ? selectedDisplayableRaces : this.races
    },

    activeRaceLabel() {
      if (this.selectedRaces.length === 1) {
        const race = this.selectedRaces[0]
        return race.startTime ? `${race.name} - ${this.$fmt.full(race.startTime)}` : race.name
      }

      if (this.selectedRaces.length > 1) {
        return `${this.selectedRaces.length} races selected`
      }

      return 'All races'
    },

    participants() {
      return this.displayedRaces
        .flatMap(race => race.participants || [])
        .filter(Boolean)
    },

    editingParticipant() {
      return this.sharedStore.state.participant[this.editingParticipantId]
    },

    checkedInCount() {
      return this.participants.filter(p => p.pickedUpAt).length
    },

    sortTitle() {
      return this.sortMenus.find(m => m.value === this.sortMode)?.title || 'Sort'
    },

    filteredParticipants() {
      const q = this.query.trim().toLowerCase()
      let rows = this.participants

      if (this.showOnlyUnchecked) {
        rows = rows.filter(p => !p.pickedUpAt)
      }

      if (q) {
        rows = rows.filter(p => [
          p.firstName,
          p.lastName,
          p.bib,
          p.waveId,
          p.shirtSize,
          p.notes,
        ].some(value => String(value || '').toLowerCase().includes(q)))
      }

      return [...rows].sort(this.participantSorter)
    },
  },

  watch: {
    raceEventId: 'load',
    raceId: 'load',
  },

  mounted() {
    eventbus.on('selectParticipant', this.selectParticipantFromEvent)
    eventbus.on('participantSearchQuery', this.setParticipantSearchQuery)
    this.load()
  },

  unmounted() {
    eventbus.removeListener('selectParticipant', this.selectParticipantFromEvent)
    eventbus.removeListener('participantSearchQuery', this.setParticipantSearchQuery)
  },

  methods: {
    async load() {
      await this.sharedStore.loadRaceRegistration(this.raceEventId, this.raceId)
    },

    setParticipantSearchQuery(query) {
      this.query = query || ''
    },

    async editParticipant(participant) {
      this.editingParticipantId = participant.id
      await this.sharedStore.loadParticipantDetail(this.raceEventId, participant.id)
    },

    selectParticipantFromEvent(participant) {
      if (!this.displayedRaces.some(race => String(race.id) === String(participant?.raceId))) return
      this.editParticipant(participant)
    },

    participantSaved(participant) {
      this.editingParticipantId = participant?.id || this.editingParticipantId
    },

    newRaceDayParticipant() {
      const participant = this.sharedStore.newRaceDayParticipant(this.defaultRaceForNewParticipant())
      this.editingParticipantId = participant.id
    },

    async toggleParticipantCheckIn(participant) {
      await this.sharedStore.setParticipantPickedUp(
        participant,
        !participant.pickedUpAt,
        { raceEventId: this.raceEventId }
      )
    },

    defaultRaceForNewParticipant() {
      if (this.selectedRaces.length === 1) return this.selectedRaces[0]
      return this.displayedRaces[0]
    },

    participantSorter(a, b) {
      if (this.sortMode === 'bib') {
        return this.bibValue(a.bib) - this.bibValue(b.bib) ||
          String(a.bib || '').localeCompare(String(b.bib || ''))
      }

      if (this.sortMode === 'shirt') {
        return String(a.shirtSize || '').localeCompare(String(b.shirtSize || '')) ||
          this.nameSort(a, b)
      }

      if (this.sortMode === 'wave') {
        return String(a.waveId || '').localeCompare(String(b.waveId || '')) ||
          this.bibValue(a.bib) - this.bibValue(b.bib)
      }

      return this.nameSort(a, b)
    },

    nameSort(a, b) {
      return String(a.lastName || '').localeCompare(String(b.lastName || '')) ||
        String(a.firstName || '').localeCompare(String(b.firstName || ''))
    },

    bibValue(bib) {
      const digits = String(bib || '').replace(/\D/g, '')
      return digits ? Number(digits) : Number.MAX_SAFE_INTEGER
    },
  },
}
</script>
